// =====================================================
// ISSUE 3 FIX: PDF Transcript Download
// Add this utility file: frontend/src/utils/pdfGenerator.js
// =====================================================

// First, install jspdf:
// npm install jspdf

import { jsPDF } from 'jspdf';

/**
 * Generate PDF transcript from session data
 * @param {Object} session - Session data object
 * @returns {void} - Downloads PDF file
 */
export const generateTranscriptPDF = (session) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 20;
  const maxWidth = pageWidth - (margin * 2);
  let yPosition = 20;
  const lineHeight = 7;
  
  // Helper: Add text with word wrap
  const addWrappedText = (text, x, y, maxW, fontSize = 11, color = [0, 0, 0]) => {
    doc.setFontSize(fontSize);
    doc.setTextColor(...color);
    const lines = doc.splitTextToSize(text, maxW);
    doc.text(lines, x, y);
    return y + (lines.length * (fontSize * 0.4));
  };
  
  // Helper: Check if we need a new page
  const checkNewPage = (neededSpace) => {
    if (yPosition + neededSpace > doc.internal.pageSize.getHeight() - 20) {
      doc.addPage();
      yPosition = 20;
      return true;
    }
    return false;
  };
  
  // Helper: Draw a horizontal line
  const drawLine = (y) => {
    doc.setDrawColor(200, 200, 200);
    doc.line(margin, y, pageWidth - margin, y);
  };

  // ============================================
  // HEADER
  // ============================================
  
  // Logo/Brand
  doc.setFontSize(24);
  doc.setTextColor(245, 158, 11); // Amber color
  doc.setFont('helvetica', 'bold');
  doc.text('Career-Coach.AI', margin, yPosition);
  yPosition += 10;
  
  // Subtitle
  doc.setFontSize(12);
  doc.setTextColor(100, 100, 100);
  doc.setFont('helvetica', 'normal');
  doc.text('Interview Transcript & Evaluation Report', margin, yPosition);
  yPosition += 15;
  
  drawLine(yPosition);
  yPosition += 10;
  
  // ============================================
  // SESSION INFO
  // ============================================
  
  doc.setFontSize(14);
  doc.setTextColor(0, 0, 0);
  doc.setFont('helvetica', 'bold');
  doc.text('Session Details', margin, yPosition);
  yPosition += 8;
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(60, 60, 60);
  
  const sessionInfo = [
    `Interview Type: ${session.interview_type || 'Text'}`,
    `Duration: ${session.duration} minutes`,
    `Interviewer Style: ${session.interviewer_personality || 'Friendly'}`,
    `Experience Level: ${session.experience_level || 'Intermediate'}`,
    `Date: ${session.created_at ? new Date(session.created_at).toLocaleDateString() : 'N/A'}`,
    `Status: ${session.status || 'Completed'}`
  ];
  
  if (session.category_name) {
    sessionInfo.push(`Category: ${session.category_name}`);
  }
  if (session.company_name) {
    sessionInfo.push(`Company: ${session.company_name}`);
  }
  
  sessionInfo.forEach(info => {
    doc.text(info, margin, yPosition);
    yPosition += 6;
  });
  
  yPosition += 10;
  drawLine(yPosition);
  yPosition += 10;
  
  // ============================================
  // SCORE
  // ============================================
  
  const score = session.overall_score || session.score?.overall;
  
  if (score) {
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'bold');
    doc.text('Overall Score', margin, yPosition);
    yPosition += 10;
    
    // Score circle representation
    doc.setFontSize(36);
    const scoreColor = score >= 80 ? [34, 197, 94] : score >= 60 ? [245, 158, 11] : [239, 68, 68];
    doc.setTextColor(...scoreColor);
    doc.text(`${score}%`, margin, yPosition);
    
    // Score breakdown
    const breakdown = session.score_breakdown || session.score?.breakdown;
    if (breakdown) {
      doc.setFontSize(10);
      doc.setTextColor(60, 60, 60);
      doc.setFont('helvetica', 'normal');
      
      let xOffset = margin + 50;
      Object.entries(breakdown).forEach(([key, value]) => {
        if (xOffset > pageWidth - 80) {
          xOffset = margin + 50;
          yPosition += 6;
        }
        doc.text(`${key}: ${value}%`, xOffset, yPosition - 20);
        xOffset += 45;
      });
    }
    
    yPosition += 10;
    drawLine(yPosition);
    yPosition += 10;
  }
  
  // ============================================
  // FEEDBACK
  // ============================================
  
  const feedback = typeof session.feedback === 'string' 
    ? JSON.parse(session.feedback) 
    : session.feedback;
    
  if (feedback) {
    checkNewPage(60);
    
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'bold');
    doc.text('Feedback', margin, yPosition);
    yPosition += 8;
    
    // Summary
    if (feedback.summary) {
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(60, 60, 60);
      yPosition = addWrappedText(feedback.summary, margin, yPosition, maxWidth, 10, [60, 60, 60]);
      yPosition += 8;
    }
    
    // Strengths
    if (feedback.strengths && feedback.strengths.length > 0) {
      checkNewPage(40);
      doc.setFontSize(11);
      doc.setTextColor(34, 197, 94);
      doc.setFont('helvetica', 'bold');
      doc.text('✓ Strengths', margin, yPosition);
      yPosition += 6;
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(60, 60, 60);
      feedback.strengths.forEach(item => {
        checkNewPage(10);
        yPosition = addWrappedText(`• ${item}`, margin + 5, yPosition, maxWidth - 5, 10, [60, 60, 60]);
        yPosition += 2;
      });
      yPosition += 5;
    }
    
    // Improvements
    if (feedback.improvements && feedback.improvements.length > 0) {
      checkNewPage(40);
      doc.setFontSize(11);
      doc.setTextColor(245, 158, 11);
      doc.setFont('helvetica', 'bold');
      doc.text('↑ Areas for Improvement', margin, yPosition);
      yPosition += 6;
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(60, 60, 60);
      feedback.improvements.forEach(item => {
        checkNewPage(10);
        yPosition = addWrappedText(`• ${item}`, margin + 5, yPosition, maxWidth - 5, 10, [60, 60, 60]);
        yPosition += 2;
      });
      yPosition += 5;
    }
    
    // Next Steps
    if (feedback.nextSteps && feedback.nextSteps.length > 0) {
      checkNewPage(40);
      doc.setFontSize(11);
      doc.setTextColor(59, 130, 246);
      doc.setFont('helvetica', 'bold');
      doc.text('→ Next Steps', margin, yPosition);
      yPosition += 6;
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(60, 60, 60);
      feedback.nextSteps.forEach(item => {
        checkNewPage(10);
        yPosition = addWrappedText(`• ${item}`, margin + 5, yPosition, maxWidth - 5, 10, [60, 60, 60]);
        yPosition += 2;
      });
    }
    
    yPosition += 10;
    drawLine(yPosition);
    yPosition += 10;
  }
  
  // ============================================
  // TRANSCRIPT
  // ============================================
  
  const transcript = session.transcript || [];
  
  if (transcript.length > 0) {
    checkNewPage(30);
    
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'bold');
    doc.text('Interview Transcript', margin, yPosition);
    yPosition += 10;
    
    transcript.forEach((msg, index) => {
      checkNewPage(30);
      
      // Role header
      const isUser = msg.role === 'user';
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(isUser ? 245 : 100, isUser ? 158 : 100, isUser ? 11 : 100);
      doc.text(isUser ? 'YOU' : 'INTERVIEWER', margin, yPosition);
      yPosition += 5;
      
      // Message content - strip markdown for cleaner PDF
      let content = msg.content || '';
      content = content
        .replace(/\*\*/g, '')  // Remove bold markers
        .replace(/\*/g, '')    // Remove italic markers
        .replace(/---/g, '')   // Remove horizontal rules
        .replace(/•/g, '-')    // Replace bullets
        .replace(/📊|🎯|📋|💡/g, ''); // Remove emojis
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(40, 40, 40);
      
      // Word wrap long messages
      const lines = doc.splitTextToSize(content, maxWidth);
      lines.forEach(line => {
        checkNewPage(8);
        doc.text(line, margin, yPosition);
        yPosition += 5;
      });
      
      yPosition += 5;
    });
  }
  
  // ============================================
  // FOOTER
  // ============================================
  
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(
      `Page ${i} of ${pageCount} | Generated by Career-Coach.AI | ${new Date().toLocaleDateString()}`,
      pageWidth / 2,
      doc.internal.pageSize.getHeight() - 10,
      { align: 'center' }
    );
  }
  
  // ============================================
  // DOWNLOAD
  // ============================================
  
  const fileName = `CareerCoach_Transcript_${session.id?.substring(0, 8) || 'session'}_${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(fileName);
};

export default generateTranscriptPDF;